import { useCallback, type ChangeEvent, useState } from 'react'

import { Checkbox, Tooltip } from '@loadsmart/loadsmart-ui'

import { Icon } from 'core/ui/components/icon/Icon'

import { isProposalClosed, isProposalSubmitted, type Proposal } from '../domain/Proposal'
import { formatContractDeadline } from '../presenters/rfpPresenter'
import { AutoSubmitConfirmDialog } from './AutoSubmitConfirmDialog'

export type AutoSubmitRatesProps = {
  autoSubmit: boolean
  disabled: boolean
  proposal: Proposal
  onToggleAutoSubmitProposal: (autoSubmit: boolean) => void
}

export function AutoSubmitRates({
  autoSubmit,
  disabled,
  proposal,
  onToggleAutoSubmitProposal,
}: AutoSubmitRatesProps) {
  const isSubmitted = isProposalSubmitted(proposal)
  const isClosed = isProposalClosed(proposal)
  const disableAutoSubmit = disabled || isClosed
  const contractDeadline = formatContractDeadline(proposal.rfp.requestDeadline)
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleAutoSubmitCheck = useCallback(
    ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
      if (currentTarget.checked) {
        setDialogOpen(true)
        return
      }

      onToggleAutoSubmitProposal(false)
    },
    [onToggleAutoSubmitProposal]
  )

  function handleEnableAutoSubmit() {
    onToggleAutoSubmitProposal(true)
    setDialogOpen(false)
  }

  function handleCancelAutoSubmit() {
    setDialogOpen(false)
  }

  if (isSubmitted) return null

  return (
    <div>
      <Checkbox
        disabled={disableAutoSubmit}
        checked={autoSubmit}
        onChange={handleAutoSubmitCheck}
        trailing={
          <Tooltip
            message="Leave this checked to automatically submit your bids at the deadline date"
            position="bottom"
          >
            <Icon name="question-circle" />
          </Tooltip>
        }
      >
        {`Auto-submit on ${contractDeadline}`}
      </Checkbox>
      <AutoSubmitConfirmDialog
        open={dialogOpen}
        onCancel={handleCancelAutoSubmit}
        onEnable={handleEnableAutoSubmit}
      />
    </div>
  )
}
